import { CustomizationType } from '../..';
import { GridInput } from '../grid';
import { DecimalValue } from '../decimal';
import { ReceivedFileInterface as File } from '../../types/FileInterface';

export interface TrackingUrl {
    trackingNumber: string;
    url: string;
}

export interface Order {
    id: number | undefined
    ;
    eshopOrderId: string;
    invoiceDate: string;
    requiredExpeditionDate: string | null;
    invoiceNumber: string;
    trackingNumbers: string[];
    trackingUrls: TrackingUrl[];
    returnTrackingUrls: TrackingUrl[];
    exchangeTrackingUrl: string | null;
    timestamp: string;
    deliveredAt: string | null;
    updatedAt: string;
    partnerOrderId: string;
    status: string;
    recipientName: string;
    recipientAttentionName: string;
    recipientEmail: string;
    recipientPhone: string;
    recipientCompanyName: string;
    recipientCompanyId: string;
    recipientTaxId: string;
    recipientStreet: string;
    recipientAddressAdditionalInfo: string;
    recipientAddressAdditionalInfo2: string | null;
    recipientCity: string;
    recipientState: string;
    recipientProvinceCode: string;
    recipientPostCode: string;
    recipientGln: string;
    billingName: string;
    billingAttentionName: string;
    billingEmail: string;
    billingPhone: string;
    billingCompanyName: string;
    billingCompanyId: string;
    billingTaxId: string;
    billingStreet: string;
    billingAddressAdditionalInfo: string;
    billingAddressAdditionalInfo2: string | null;
    billingCity: string;
    billingState: string;
    billingProvinceCode: string;
    billingPostCode: string;
    billingGln: string;
    shipper: string;
    price: DecimalValue;
    shippingPrice: DecimalValue;
    currency: string;
    paid: boolean;
    paymentMethod: string;
    shipperServiceCode: string;
    branchId: string | null;
    codPrice: DecimalValue;
    codCurrency: string;
    codRefNumber: string;
    message: string;
    addressId: string;
    orderType: string;
    note: string;
    printFiles?: boolean;
    holdByQuality?: boolean;
    pickFromCustomsWarehouse?: boolean;
    disableShippingReorder?: boolean;
    underSanction?: boolean;
    allowExpedition: boolean;
    printReturnLabel: boolean;
    marketplace: string | null;
    waitingForPaymentAt: string | null;
    productsNotAvailableAt: string | null;
    suspendedAt: string | null;
    releaseFromSuspendedAt: string | null;
    preorderReleaseAt: string | null;
    incorrectAt: string | null;
    waitingForShipmentAt: string | null;
    shipmentOrderedAt: string | null;
    inProcessAt: string | null;
    packedAt: string | null;
    waitingForCarrierAt: string | null;
    shippedAt: string | null;
    atPickupPointAt: string | null;
    returningBackAt: string | null;
    returnedBackAt: string | null;
    preReceivedAt: string | null;
    canceledAt: string | null;
    /** Deadline by which Skladon has to hand the order over to the carrier. */
    slaDate: string | null;
    /** When the order was first scanned in the warehouse. */
    scannedAt: string | null;
    /** System the order originated from. */
    sourceSys: string | null;
    /** Total weight of the shipment in kilograms, filled in once the order has been packed. */
    weight: number | null;

    parentOrderId: number | null;
    parentEshopOrderId: string | null;
    returnTrackingNumber: string | null;
    exchangeTrackingNumber: string | null;
    eshopNote: string | null | undefined;
    orderBoxCount: number | null;
    incorrectOrderMessage: string | null;
    orderProducts?: OrderProduct[];
    orderFiles?: File[];
}

export interface OrderProduct {
    orderProductFiles?: {
        id?: number;
        type: string;
        data?: string;
        fileType: string;
        fileName: string;
        fileSize?: string | number;
        orderProductId?: number;
    }[];
    sku: string;
    count: DecimalValue;
    productPrice: DecimalValue | null;
    lot: string | null;
    locationId: string | null;
    lineState: string | null;
    serialNumbers: string[];
    customsProductType: string | null;
    packingLists?: PackingList[];
    customizationType?: CustomizationType | string | null;
    customizationText?: string | null;
    bcxCodes?: string[] | null;
    confirmed?: string | null;
    missingCount?: DecimalValue | null;
    eshopOrderProductId?: string | null;
}

export interface OrdersGridOutput {
    data: Order[];
    request: GridInput;
    totalCount: number;
}

export const fromCoreOrderNames = {
    ID: 'id',
    ADDRESS_ID: 'addressId',
    ALLOW_EXPEDITION: 'allowExpedition',
    BILLING_ADDRESS_ADDITIONAL_INFO: 'billingAddressAdditionalInfo',
    BILLING_ATTENTION_NAME: 'billingAttentionName',
    BILLING_CITY: 'billingCity',
    BILLING_COMPANY_ID: 'billingCompanyId',
    BILLING_COMPANY_NAME: 'billingCompanyName',
    BILLING_EMAIL: 'billingEmail',
    BILLING_NAME: 'billingName',
    BILLING_PHONE: 'billingPhone',
    BILLING_POST_CODE: 'billingPostCode',
    BILLING_PROVINCE_CODE: 'billingProvinceCode',
    BILLING_STATE: 'billingState',
    BILLING_STREET: 'billingStreet',
    BILLING_TAX_ID: 'billingTaxId',
    COD_CURRENCY: 'codCurrency',
    COD_PRICE: 'codPrice',
    COD_REF_NUMBER: 'codRefNumber',
    CURRENCY: 'currency',
    ESHOP_ORDER_ID: 'eshopOrderId',
    INVOICE_DATE: 'invoiceDate',
    REQUIRED_EXPEDITION_DATE: 'requiredExpeditionDate',
    INVOICE_NUMBER: 'invoiceNumber',
    MARKETPLACE: 'marketplace',
    MESSAGE: 'message',
    METHOD: 'method',
    NOTE: 'note',
    ORDER_TYPE: 'orderType',
    PAID: 'paid',
    PARTNER_ORDER_ID: 'partnerOrderId',
    PAYMENT_METHOD: 'paymentMethod',
    PRICE: 'price',
    PRINT_FILES: 'printFiles',
    HOLD_BY_QUALITY: 'holdByQuality',
    PICK_FROM_CUSTOMS_WAREHOUSE: 'pickFromCustomsWarehouse',
    UNDER_SANCTION: 'underSanction',
    RECIPIENT_ADDRESS_ADDITIONAL_INFO: 'recipientAddressAdditionalInfo',
    RECIPIENT_ATTENTION_NAME: 'recipientAttentionName',
    RECIPIENT_CITY: 'recipientCity',
    RECIPIENT_COMPANY_ID: 'recipientCompanyId',
    RECIPIENT_COMPANY_NAME: 'recipientCompanyName',
    RECIPIENT_EMAIL: 'recipientEmail',
    RECIPIENT_NAME: 'recipientName',
    RECIPIENT_PHONE: 'recipientPhone',
    RECIPIENT_POST_CODE: 'recipientPostCode',
    RECIPIENT_PROVINCE_CODE: 'recipientProvinceCode',
    RECIPIENT_STATE: 'recipientState',
    RECIPIENT_STREET: 'recipientStreet',
    RECIPIENT_TAX_ID: 'recipientTaxId',
    SHIPPER: 'shipper',
    SHIPPER_SERVICE_CODE: 'shipperServiceCode',
    BRANCH_ID: 'branchId',
    SHIPPING_PRICE: 'shippingPrice',
    STATUS: 'status',
    TIMESTAMP: 'timestamp',
    TRACKING_NUMBER: 'trackingNumbers',
    RECIPIENT_GLN: 'recipientGln',
    BILLING_GLN: 'billingGln',
    DELIVERED_AT: 'deliveredAt',
    UPDATED_AT: 'updatedAt',
    DISABLE_SHIPPING_REORDER: 'disableShippingReorder',
    WAITING_FOR_PAYMENT_AT: 'waitingForPaymentAt',
    PRODUCTS_NOT_AVAILABLE_AT: 'productsNotAvailableAt',
    SUSPENDED_AT: 'suspendedAt',
    RELEASE_FROM_SUSPENDED_AT: 'releaseFromSuspendedAt',
    INCORRECT_AT: 'incorrectAt',
    WAITING_FOR_SHIPMENT_AT: 'waitingForShipmentAt',
    SHIPMENT_ORDERED_AT: 'shipmentOrderedAt',
    IN_PROCESS_AT: 'inProcessAt',
    PACKED_AT: 'packedAt',
    WAITING_FOR_CARRIER_AT: 'waitingForCarrierAt',
    CANCELED_AT: 'canceledAt',

    SHIPPED_AT: 'shippedAt',
    AT_PICKUP_POINT_AT: 'atPickupPointAt',
    RETURNING_BACK_AT: 'returningBackAt',
    RETURNED_BACK_AT: 'returnedBackAt',
    PRERECEIVED_AT: 'preReceivedAt',
    PARENT_ORDER_ID: 'parentOrderId',
    PARENT_ESHOP_ORDER_ID: 'parentEshopOrderId',
    RETURN_TRACKING_NUMBER: 'returnTrackingNumber',
    EXCHANGE_TRACKING_NUMBER: 'exchangeTrackingNumber',
    ESHOP_NOTE: 'eshopNote',
    ORDER_BOX_COUNT: 'orderBoxCount',
    INCORRECT_ORDER_MESSAGE: 'incorrectOrderMessage',
    SLA_DATE: 'slaDate',
    SCANNED_AT: 'scannedAt',
    PREORDER_RELEASE_AT: 'preorderReleaseAt',
    PRINT_RETURN_LABEL: 'printReturnLabel',
    SOURCE_SYS: 'sourceSys',
    WEIGHT: 'weight',
};

export const toCoreOrderNames = {
    id: 'ID',
    addressId: 'ADDRESS_ID',
    allowExpedition: 'ALLOW_EXPEDITION',
    billingAddressAdditionalInfo: 'BILLING_ADDRESS_ADDITIONAL_INFO',
    billingAttentionName: 'BILLING_ATTENTION_NAME',
    billingCity: 'BILLING_CITY',
    billingCompanyId: 'BILLING_COMPANY_ID',
    billingCompanyName: 'BILLING_COMPANY_NAME',
    billingEmail: 'BILLING_EMAIL',
    billingName: 'BILLING_NAME',
    billingPhone: 'BILLING_PHONE',
    billingPostCode: 'BILLING_POST_CODE',
    billingProvinceCode: 'BILLING_PROVINCE_CODE',
    billingState: 'BILLING_STATE',
    billingStreet: 'BILLING_STREET',
    billingTaxId: 'BILLING_TAX_ID',
    codCurrency: 'COD_CURRENCY',
    codPrice: 'COD_PRICE',
    codRefNumber: 'COD_REF_NUMBER',
    currency: 'CURRENCY',
    eshopOrderId: 'ESHOP_ORDER_ID',
    invoiceDate: 'INVOICE_DATE',
    requiredExpeditionDate: 'REQUIRED_EXPEDITION_DATE',
    invoiceNumber: 'INVOICE_NUMBER',
    marketplace: 'MARKETPLACE',
    message: 'MESSAGE',
    method: 'METHOD',
    note: 'NOTE',
    orderType: 'ORDER_TYPE',
    paid: 'PAID',
    partnerOrderId: 'PARTNER_ORDER_ID',
    paymentMethod: 'PAYMENT_METHOD',
    price: 'PRICE',
    printFiles: 'PRINT_FILES',
    holdByQuality: 'HOLD_BY_QUALITY',
    pickFromCustomsWarehouse: 'PICK_FROM_CUSTOMS_WAREHOUSE',
    underSanction: 'UNDER_SANCTION',
    recipientAddressAdditionalInfo: 'RECIPIENT_ADDRESS_ADDITIONAL_INFO',
    recipientAttentionName: 'RECIPIENT_ATTENTION_NAME',
    recipientCity: 'RECIPIENT_CITY',
    recipientCompanyId: 'RECIPIENT_COMPANY_ID',
    recipientCompanyName: 'RECIPIENT_COMPANY_NAME',
    recipientEmail: 'RECIPIENT_EMAIL',
    recipientName: 'RECIPIENT_NAME',
    recipientPhone: 'RECIPIENT_PHONE',
    recipientPostCode: 'RECIPIENT_POST_CODE',
    recipientProvinceCode: 'RECIPIENT_PROVINCE_CODE',
    recipientState: 'RECIPIENT_STATE',
    recipientStreet: 'RECIPIENT_STREET',
    recipientTaxId: 'RECIPIENT_TAX_ID',
    shipper: 'SHIPPER',
    shipperServiceCode: 'SHIPPER_SERVICE_CODE',
    branchId: 'BRANCH_ID',
    shippingPrice: 'SHIPPING_PRICE',
    status: 'STATUS',
    timestamp: 'TIMESTAMP',
    trackingNumbers: 'TRACKING_NUMBER',
    recipientGln: 'RECIPIENT_GLN',
    billingGln: 'BILLING_GLN',
    deliveredAt: 'DELIVERED_AT',
    updatedAt: 'UPDATED_AT',
    disableShippingReorder: 'DISABLE_SHIPPING_REORDER',
    waitingForPaymentAt: 'WAITING_FOR_PAYMENT_AT',
    productsNotAvailableAt: 'PRODUCTS_NOT_AVAILABLE_AT',
    suspendedAt: 'SUSPENDED_AT',
    releaseFromSuspendedAt: 'RELEASE_FROM_SUSPENDED_AT',
    incorrectAt: 'INCORRECT_AT',
    waitingForShipmentAt: 'WAITING_FOR_SHIPMENT_AT',
    shipmentOrderedAt: 'SHIPMENT_ORDERED_AT',
    inProcessAt: 'IN_PROCESS_AT',
    packedAt: 'PACKED_AT',
    waitingForCarrierAt: 'WAITING_FOR_CARRIER_AT',
    shippedAt: 'SHIPPED_AT',
    atPickupPointAt: 'AT_PICKUP_POINT_AT',
    returningBackAt: 'RETURNING_BACK_AT',
    returnedBackAt: 'RETURNED_BACK_AT',
    preReceivedAt: 'PRERECEIVED_AT',
    parentOrderId: 'PARENT_ORDER_ID',
    parentEshopOrderId: 'PARENT_ESHOP_ORDER_ID',
    returnTrackingNumber: 'RETURN_TRACKING_NUMBER',
    exchangeTrackingNumber: 'EXCHANGE_TRACKING_NUMBER',
    eshopNote: 'ESHOP_NOTE',
    orderBoxCount: 'ORDER_BOX_COUNT',
    incorrectOrderMessage: 'INCORRECT_ORDER_MESSAGE',
    canceledAt: 'CANCELED_AT',
    slaDate: 'SLA_DATE',
    scannedAt: 'SCANNED_AT',
    preorderReleaseAt: 'PREORDER_RELEASE_AT',
    printReturnLabel: 'PRINT_RETURN_LABEL',
    sourceSys: 'SOURCE_SYS',
    weight: 'WEIGHT',
};

export interface PackingList {
    id: number;
    expeditionPalletNumber: string | null;
    expeditionBoxNumber: string;
    ssccCode: string | null;
    serialNumbers: string[];
    lots?: string[];
    expDates?: string[];
    count: number;
}
